/**
 * Console prototype hub.
 *
 * Routes:
 *   /console                   -> landing (4 prototype tiles)
 *   /console/dashboard         -> ConsolePageDashboard inside ConsoleShell
 *   /console/calls             -> ConsolePageCalls inside ConsoleShell
 *   /console/access-lists      -> ConsolePageAccessLists inside ConsoleShell
 *
 * Each shelled page provides nav groups, breadcrumb, and session/env so the
 * chrome looks identical to the eventual ops-console.
 */

import type { ReactNode } from "react";
import { Link, Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import {
  Phone,
  Inbox,
  Layers3,
  Settings2,
  ShieldCheck,
  PanelTop,
  ListChecks,
  PhoneCall,
  Activity,
  ArrowRight,
  ArrowLeft,
  Hammer,
  ExternalLink,
} from "lucide-react";

import {
  ConsoleShell,
  type ConsoleNavGroup,
  type ConsoleBreadcrumb,
  type ConsoleSessionInfo,
  type ConsoleEnvInfo,
  ConsolePageHeader,
  ConsoleCard,
  ConsoleButton,
} from "@/design-system/shells/console-shell";
import { ConsolePageAccessLists } from "@/design-system/patterns/console-page-access-lists";
import { ConsolePageApprovals } from "@/design-system/patterns/console-page-approvals";
import { ConsolePageCalls } from "@/design-system/patterns/console-page-calls";
import { ConsolePageCatalog } from "@/design-system/patterns/console-page-catalog";
import { ConsolePageDashboard } from "@/design-system/patterns/console-page-dashboard";
import { ConsolePageHelp } from "@/design-system/patterns/console-page-help";
import { ConsolePageHotlines } from "@/design-system/patterns/console-page-hotlines";
import { ConsolePagePreferences } from "@/design-system/patterns/console-page-preferences";
import { ConsolePagePublish } from "@/design-system/patterns/console-page-publish";
import { ConsolePageRuntime } from "@/design-system/patterns/console-page-runtime";
import { ConsolePageTransport } from "@/design-system/patterns/console-page-transport";
import { cn } from "@/app/components/ui/utils";

const NAV_GROUPS: ConsoleNavGroup[] = [
  {
    key: "general",
    label: "概览",
    description: "本机健康、传输与运行时",
    items: [
      { href: "/console/dashboard", label: "工作台", icon: PanelTop },
      { href: "/console/transport", label: "传输层", icon: Activity },
      { href: "/console/runtime", label: "运行时", icon: Hammer },
    ],
  },
  {
    key: "caller",
    label: "调用方",
    description: "让 Agent 帮你调用 Hotline",
    items: [
      { href: "/console/catalog", label: "热线目录", icon: Layers3 },
      { href: "/console/calls", label: "调用记录", icon: PhoneCall },
      { href: "/console/approvals", label: "审批中心", icon: Inbox, badge: 2 },
      { href: "/console/access-lists", label: "名单管理", icon: ShieldCheck },
      { href: "/console/preferences", label: "偏好", icon: Settings2 },
    ],
  },
  {
    key: "responder",
    label: "响应方",
    description: "把你写的能力发布成 Hotline",
    items: [
      { href: "/console/hotlines", label: "Hotline 管理", icon: ListChecks, badge: 3 },
      { href: "/console/publish", label: "平台发布", icon: Phone },
    ],
  },
];

const SESSION: ConsoleSessionInfo = {
  label: "会话已解锁",
  tone: "ok",
  hint: "上次刷新 14:08:00 (4min ago)",
};

const ENV: ConsoleEnvInfo = {
  label: "Local · dev",
  detail: "stdio + http://127.0.0.1:8092",
};

const PROTOTYPES: {
  href: string;
  title: string;
  kicker: string;
  description: string;
  specAnchor: string;
  status: "ready" | "planned";
  preview: ReactNode;
}[] = [
  {
    href: "/console/dashboard",
    title: "工作台 · Dashboard",
    kicker: "GENERAL",
    description:
      "条件引导（caller 注册 / 平台模式）+ 4 进程健康灯 + 真实 4 步上手清单 + 服务健康度表（带重启入口）+ Responder 摘要。绑死 GET /status。",
    specAnchor: "1--generaldashboardpage--工作台",
    status: "ready",
    preview: <DashboardThumb />,
  },
  {
    href: "/console/calls",
    title: "调用记录 · Calls",
    kicker: "CALLER",
    description:
      "人话通话日志：每行 = HotlineDisplayName + 提炼后的动作短语；右侧 detail 三段式（摘要 / 请求背景 / 请求结果），raw JSON 是折叠 toggle。新调用入口在 Catalog；审批不在本页。",
    specAnchor: "2--callercallspage--调用记录",
    status: "ready",
    preview: <CallsThumb />,
  },
  {
    href: "/console/access-lists",
    title: "名单管理 · Access Lists",
    kicker: "CALLER",
    description:
      "3 tab（Responder 白 / Hotline 白 / 黑名单）+ 真实 3 模式（manual / allow_listed / allow_all）+ 「未生效」chip。所有 ID 用真实风格。",
    specAnchor: "3--calleraccesslistspage--名单管理",
    status: "ready",
    preview: <AccessListsThumb />,
  },
  {
    href: "/console/approvals",
    title: "审批中心 · Approvals",
    kicker: "CALLER",
    description:
      "5 filter tab + ApprovalCard（risk factors / 三按钮）+ ExecutionBlock。演示 §4.0b 审批疲劳 banner（队列堆积）+ §4.3 加白名单后教育 popover（mode 两态对比）。",
    specAnchor: "4--callerapprovalspage--审批中心",
    status: "ready",
    preview: <ApprovalsThumb />,
  },
  {
    href: "/console/catalog",
    title: "热线目录 · Catalog",
    kicker: "CALLER",
    description:
      "唯一发起调用入口。master-detail + TryCallDrawer 表单按 hotline 的 input_fields 自动渲染。空态走「让 Responder 发布 / 开平台模式 / 一键加 demo」三路；支持 ?from / ?hotline_id / ?prefill 深链。",
    specAnchor: "52-callercatalogpage--热线目录",
    status: "ready",
    preview: <CatalogThumb />,
  },
  {
    href: "/console/help",
    title: "上手 & 帮助 · Help",
    kicker: "GENERAL",
    description:
      "内置使用指南。sticky TOC + 8 篇文章（什么是 Caller/Responder · 5min 跑通第一次 · 审批与白名单 · 故障排查 · 反馈渠道）。Sidebar 帮助入口直达本页。",
    specAnchor: "57-help--内置上手--帮助页",
    status: "ready",
    preview: <HelpThumb />,
  },
  {
    href: "/console/transport",
    title: "传输配置 · Transport",
    kicker: "GENERAL",
    description:
      "三种通信类型（local · relay_http · email）三选一卡 + 条件子表单 + 「测试连接」+ 当前配置 JSON 预览。绑死 POST /runtime/transport 与 /runtime/transport/test。",
    specAnchor: "56-generaltransportpage--传输配置",
    status: "ready",
    preview: <TransportThumb />,
  },
  {
    href: "/console/runtime",
    title: "运行时监控 · Runtime",
    kicker: "GENERAL",
    description:
      "service tab（caller/responder/relay）+ 进程健康卡（PID/uptime/last_error）+ 终端样式日志（filter/level/dedup/时间分隔符）+ Debug Snapshot 折叠。每 5s silent 拉一次。",
    specAnchor: "55-generalruntimepage--runtime-监控",
    status: "ready",
    preview: <RuntimeThumb />,
  },
  {
    href: "/console/preferences",
    title: "偏好设置 · Preferences",
    kicker: "CALLER",
    description:
      "GlobalPolicyCard（manual / allow_listed / allow_all 三选一 + 切换 destructive 弹 ModeChangeImpact 横幅）+ 自动路由偏好（task_type → hotline_id 表）+ 名单计数跳转。",
    specAnchor: "53-callerpreferencespage--偏好设置",
    status: "ready",
    preview: <PreferencesThumb />,
  },
  {
    href: "/console/hotlines",
    title: "Hotline 管理 · Responder",
    kicker: "RESPONDER",
    description:
      "本机 Hotline 列表（display_name + ReviewBadge + adapter chip + mono id + task_types + soft/hard timeout）+ enable Switch + 「草稿」5 tab Drawer（概览/契约/示例/使用建议/原始）。",
    specAnchor: "54-responderresponderhotlinespage--hotline-管理",
    status: "ready",
    preview: <HotlinesThumb />,
  },
  {
    href: "/console/publish",
    title: "平台发布 · Publish",
    kicker: "RESPONDER",
    description:
      "4 列 Kanban（草稿 / 审核中 / 需修改 / 已上架）+ 选中卡片 review timeline（含审核员 note）+ 自动检查清单 + 操作按钮（提交 / 撤回 / 修改后重提）。",
    specAnchor: "54-responderresponderhotlinespage--hotline-管理",
    status: "ready",
    preview: <PublishThumb />,
  },
];

export default function ConsoleRoot() {
  return (
    <div className="console-mode min-h-screen flex flex-col bg-[var(--surface)]">
      <ConsoleSiteBar />
      <div className="flex-1 flex flex-col min-h-0">
        <ConsoleRoutes />
      </div>
    </div>
  );
}

function ConsoleRoutes() {
  return (
    <Routes>
      <Route index element={<ConsoleLanding />} />
      <Route
        path="dashboard"
        element={
          <ConsoleShellRoute crumb={[{ label: "概览", href: "/console" }, { label: "工作台" }]}>
            <ConsolePageDashboard />
          </ConsoleShellRoute>
        }
      />
      <Route
        path="calls"
        element={
          <ConsoleShellRoute crumb={[{ label: "调用方", href: "/console" }, { label: "调用记录" }]}>
            <ConsolePageCalls />
          </ConsoleShellRoute>
        }
      />
      <Route
        path="access-lists"
        element={
          <ConsoleShellRoute crumb={[{ label: "调用方", href: "/console" }, { label: "审批策略与名单" }]}>
            <ConsolePageAccessLists />
          </ConsoleShellRoute>
        }
      />
      <Route
        path="catalog"
        element={
          <ConsoleShellRoute crumb={[{ label: "调用方", href: "/console" }, { label: "热线目录" }]}>
            <ConsolePageCatalog />
          </ConsoleShellRoute>
        }
      />
      <Route
        path="approvals"
        element={
          <ConsoleShellRoute crumb={[{ label: "调用方", href: "/console" }, { label: "审批中心" }]}>
            <ConsolePageApprovals />
          </ConsoleShellRoute>
        }
      />
      <Route
        path="help"
        element={
          <ConsoleShellRoute crumb={[{ label: "上手 & 帮助", href: "/console" }, { label: "使用指南" }]}>
            <ConsolePageHelp />
          </ConsoleShellRoute>
        }
      />
      <Route
        path="transport"
        element={
          <ConsoleShellRoute crumb={[{ label: "概览", href: "/console" }, { label: "传输层" }]}>
            <ConsolePageTransport />
          </ConsoleShellRoute>
        }
      />
      <Route
        path="runtime"
        element={
          <ConsoleShellRoute crumb={[{ label: "概览", href: "/console" }, { label: "运行时" }]}>
            <ConsolePageRuntime />
          </ConsoleShellRoute>
        }
      />
      <Route
        path="preferences"
        element={
          <ConsoleShellRoute crumb={[{ label: "调用方", href: "/console" }, { label: "偏好" }]}>
            <ConsolePagePreferences />
          </ConsoleShellRoute>
        }
      />
      <Route
        path="hotlines"
        element={
          <ConsoleShellRoute crumb={[{ label: "响应方", href: "/console" }, { label: "Hotline 管理" }]}>
            <ConsolePageHotlines />
          </ConsoleShellRoute>
        }
      />
      <Route
        path="publish"
        element={
          <ConsoleShellRoute crumb={[{ label: "响应方", href: "/console" }, { label: "平台发布" }]}>
            <ConsolePagePublish />
          </ConsoleShellRoute>
        }
      />
      <Route path="*" element={<PlannedRoute />} />
    </Routes>
  );
}

/* ── Site bar (jump out to brand-site) ──────────────────────────────── */

const SITE_BAR_LINKS: { href: string; label: string; external?: boolean }[] = [
  { href: "/playground", label: "Playground" },
  { href: "/marketplace", label: "Marketplace" },
  { href: "/docs", label: "Docs" },
];

function ConsoleSiteBar() {
  return (
    <div
      className="h-7 flex items-center px-5 border-b bg-[var(--brand-secondary)]/40 text-[11px] shrink-0"
      style={{ borderColor: "var(--border)" }}
    >
      <Link
        to="/"
        className="inline-flex items-center gap-1 font-medium text-[var(--brand-muted)] hover:text-[var(--ink)]"
      >
        <ArrowLeft className="h-3 w-3" strokeWidth={2.5} />
        宣传站首页
      </Link>
      <span className="mx-2.5 text-[var(--brand-muted)]/40">·</span>
      <span className="font-semibold tracking-[0.12em] uppercase text-[var(--brand-muted)] text-[10px]">
        Console Mode · Prototype
      </span>

      <div className="ml-auto flex items-center gap-3.5">
        {SITE_BAR_LINKS.map((l) => (
          <Link
            key={l.href}
            to={l.href}
            className="inline-flex items-center gap-1 text-[var(--brand-muted)] hover:text-[var(--ink)] font-medium"
          >
            {l.label}
            <ExternalLink className="h-2.5 w-2.5 opacity-60" strokeWidth={2.5} />
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ── Shell wrapper that pulls active path from router ───────────────── */

function ConsoleShellRoute({
  crumb,
  children,
}: {
  crumb: ConsoleBreadcrumb[];
  children: ReactNode;
}) {
  const location = useLocation();
  const [search] = useSearchParams();
  const fromKey = search.get("from");
  const fromContext = fromKey ? { from: fromKey } : undefined;
  return (
    <ConsoleShell
      groups={NAV_GROUPS}
      activeHref={location.pathname}
      breadcrumb={crumb}
      session={SESSION}
      env={ENV}
      fromContext={fromContext}
    >
      {children}
      <BackToHub />
    </ConsoleShell>
  );
}

function BackToHub() {
  return (
    <div className="mt-10 pt-4 border-t flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
      <Link
        to="/console"
        className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[var(--brand-muted)] hover:text-[var(--ink)]"
      >
        <ArrowLeft className="h-3 w-3" />
        返回原型 hub
      </Link>
      <span className="text-[11px] text-[var(--brand-muted)]/70">
        Console-mode prototype · 静态数据 · 仅用于设计 review
      </span>
    </div>
  );
}

/* ── Landing page (prototype tiles) ─────────────────────────────────── */

function ConsoleLanding() {
  return (
    <div className="bg-[var(--surface)] text-[var(--ink)] flex-1">
      <div className="max-w-6xl mx-auto px-8 py-12">
        <div className="mb-10">
          <div className="c-kicker mb-3">CONSOLE MODE · PROTOTYPE</div>
          <h1 className="text-[34px] font-semibold tracking-tight leading-none text-[var(--ink)]">
            Delegated-Execution Console
          </h1>
          <p className="mt-3 text-[14px] text-[var(--brand-muted)] max-w-2xl leading-relaxed">
            这是 ops-console 的高保真**视觉原型**。Token、字体、圆角、状态色都来自{" "}
            <code className="console-mono text-[12.5px] bg-[var(--brand-secondary)]/60 px-1 py-0.5 rounded">
              src/styles/delexec-console-tokens.css
            </code>
            。
          </p>
          <p className="mt-2 text-[14px] text-[var(--brand-muted)] max-w-2xl leading-relaxed">
            <strong className="text-[var(--ink)]">本原型不代替业务逻辑</strong>。每个 widget「为什么存在 / 服务什么操作或观测 / 真实数据来源 / 空状态」全部写在内容契约里，落地任何一页前**必须先读**。
          </p>
        </div>

        <ConsoleCard className="mb-8 border-l-[3px] border-l-[var(--brand-orange)] bg-[color-mix(in_oklab,var(--brand-orange)_5%,transparent)]">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-4 w-4 text-[var(--brand-orange)] mt-0.5 shrink-0" strokeWidth={2.5} />
            <div className="flex-1">
              <p className="text-[13px] font-semibold text-[var(--ink)]">先读内容契约 · Content Spec</p>
              <p className="text-[12.5px] text-[var(--brand-muted)] mt-0.5 leading-relaxed">
                每页放什么、为什么放、服务什么操作或观测、数据从哪来 — 全部在{" "}
                <code className="console-mono text-[11.5px] bg-white px-1 py-0.5 rounded c-ink-border">
                  docs/console-content-spec.md
                </code>
                。原型与 spec 出现冲突，以 spec 为准。
              </p>
            </div>
            <a
              href="https://github.com/anyone/call-anything-brand-site/blob/main/docs/console-content-spec.md"
              target="_blank"
              rel="noreferrer"
              className="text-[12px] font-medium text-[var(--ink)] underline underline-offset-2 hover:text-[var(--brand-orange)] shrink-0"
            >
              打开 spec →
            </a>
          </div>
        </ConsoleCard>

        <div className="grid grid-cols-2 gap-5">
          {PROTOTYPES.map((p) => (
            <PrototypeTile key={p.href} {...p} />
          ))}
        </div>

        <div className="mt-12">
          <SystemNotes />
        </div>
      </div>
    </div>
  );
}

function PrototypeTile({
  href,
  title,
  kicker,
  description,
  specAnchor,
  status,
  preview,
}: {
  href: string;
  title: string;
  kicker: string;
  description: string;
  specAnchor: string;
  status: "ready" | "planned";
  preview: ReactNode;
}) {
  const isReady = status === "ready";
  return (
    <div
      className={cn(
        "block bg-white border overflow-hidden",
        isReady ? "c-lift" : "opacity-60",
      )}
      style={{
        borderColor: "var(--border)",
        borderRadius: "var(--c-radius)",
      }}
    >
      <div
        className="aspect-[16/9] bg-[var(--brand-secondary)]/40 border-b flex items-center justify-center overflow-hidden"
        style={{ borderColor: "var(--border)" }}
      >
        {preview}
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10.5px] font-semibold tracking-[0.14em] uppercase text-[var(--brand-muted)]">
            {kicker}
          </span>
          <span
            className={cn(
              "c-status",
              isReady ? "c-status-success" : "c-status-warn",
            )}
          >
            {isReady ? "原型可点开" : "原型未画"}
          </span>
        </div>
        <h2 className="text-[15px] font-semibold text-[var(--ink)]">{title}</h2>
        <p className="text-[12.5px] text-[var(--brand-muted)] mt-1.5 leading-relaxed">
          {description}
        </p>
        <div className="mt-3 pt-3 border-t flex items-center gap-3" style={{ borderColor: "var(--border)" }}>
          {isReady ? (
            <Link
              to={href}
              className="text-[12px] font-semibold text-[var(--ink)] inline-flex items-center gap-1 hover:text-[var(--brand-orange)]"
            >
              打开原型 <ArrowRight className="h-3 w-3" strokeWidth={2.5} />
            </Link>
          ) : (
            <span className="text-[12px] font-medium text-[var(--brand-muted)]">原型留待 Phase 2</span>
          )}
          <a
            href={`https://github.com/anyone/call-anything-brand-site/blob/main/docs/console-content-spec.md#${specAnchor}`}
            target="_blank"
            rel="noreferrer"
            className="ml-auto text-[12px] font-medium text-[var(--brand-muted)] underline underline-offset-2 hover:text-[var(--ink)] inline-flex items-center gap-1"
          >
            内容契约
          </a>
        </div>
      </div>
    </div>
  );
}

function SystemNotes() {
  return (
    <ConsoleCard className="bg-white">
      <ConsolePageHeader
        kicker="DESIGN SYSTEM · 速查"
        title="Console-mode 视觉准则"
        description="原型严格遵守的可量化规则。落地时不要偏离。"
      />
      <ul className="grid grid-cols-2 gap-x-8 gap-y-2.5 text-[12.5px] text-[var(--ink)]">
        <NoteRow label="背景" value="paper #F7F2E8 + 首页同款印花（color tiles 8% / pattern 6%；可切 subtle/off）" />
        <NoteRow label="字体" value="Space Grotesk · IBM Plex Mono 给 ID/code" />
        <NoteRow label="圆角" value="4px 默认 / 2px 小件 / 8px 大件 / 999px pill" />
        <NoteRow label="字号基线" value="14px（密度优先）" />
        <NoteRow label="活跃签名" value="1.5–2px ink 边框 + 2px 硬阴影 + 1px 抬升" />
        <NoteRow label="状态色" value="只用 --c-status-* token，禁止自由发挥 Tailwind 色板" />
        <NoteRow label="角色色" value="caller=teal · responder=orange · platform=purple · client=blue · protocol=green · selfhost=yellow" />
        <NoteRow label="主 CTA" value="brand-green 底 + ink 边框 + 抬升；危险动作走 brand-red" />
      </ul>
      <div
        className="mt-5 pt-4 border-t flex items-center gap-3 text-[12px]"
        style={{ borderColor: "var(--border)" }}
      >
        <span className="text-[var(--brand-muted)]">规范源：</span>
        <code className="console-mono text-[11.5px] bg-[var(--brand-secondary)]/60 px-1.5 py-0.5 rounded">
          src/styles/delexec-console-tokens.css
        </code>
        <code className="console-mono text-[11.5px] bg-[var(--brand-secondary)]/60 px-1.5 py-0.5 rounded">
          src/styles/console-mode.css
        </code>
      </div>
    </ConsoleCard>
  );
}

function NoteRow({ label, value }: { label: string; value: string }) {
  return (
    <li className="flex items-baseline gap-2">
      <span className="text-[var(--brand-muted)] w-20 shrink-0">{label}</span>
      <span className="text-[var(--ink)]">{value}</span>
    </li>
  );
}

/* ── Planned route fallback ─────────────────────────────────────────── */

function PlannedRoute() {
  return (
    <div className="console-mode min-h-screen bg-[var(--surface)] flex items-center justify-center">
      <div className="text-center max-w-md">
        <div className="c-kicker justify-center mb-3">PLANNED</div>
        <h1 className="text-[20px] font-semibold text-[var(--ink)]">这个原型还没搭</h1>
        <p className="text-[13px] text-[var(--brand-muted)] mt-2">
          下一轮原型迭代会补 Preferences / Approvals / Catalog / Hotlines。
        </p>
        <div className="mt-5">
          <Link to="/console">
            <ConsoleButton>
              <ArrowLeft className="h-3.5 w-3.5" />
              回到原型 hub
            </ConsoleButton>
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ── Tile thumbnail sketches ────────────────────────────────────────── */

function DashboardThumb() {
  return (
    <div className="w-full h-full p-4 flex flex-col gap-2">
      <div className="grid grid-cols-4 gap-1.5">
        {["caller", "responder", "platform", "client"].map((c) => (
          <div
            key={c}
            className="bg-white border h-9 flex items-end overflow-hidden"
            style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
          >
            <div
              className={cn(
                "w-full",
                c === "caller" && "bg-[var(--brand-teal)]/70",
                c === "responder" && "bg-[var(--brand-orange)]/70",
                c === "platform" && "bg-[var(--brand-purple)]/70",
                c === "client" && "bg-[var(--brand-blue)]/70",
              )}
              style={{ height: "55%" }}
            />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-1.5 mt-1">
        {[1, 2, 3, 4].map((n) => (
          <div
            key={n}
            className="bg-white border px-1.5 py-1 text-[8.5px] font-bold text-[var(--brand-muted)]"
            style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
          >
            {n}
          </div>
        ))}
      </div>
    </div>
  );
}

function CallsThumb() {
  const ROWS: { headline: string; meta: string; tone: "success" | "info" | "error" | "warn" }[] = [
    { headline: "Slack 通知 · #ops", meta: "刚刚 · 你 · 白名单", tone: "success" },
    { headline: "日历 · 客户拜访", meta: "1 min · agent · 你批准", tone: "info" },
    { headline: "网页搜索 · 监管", meta: "2 min · agent · 你拒绝", tone: "error" },
    { headline: "文档总结 · Q1", meta: "5 min · 你 · 无需审批", tone: "success" },
  ];
  const DOT: Record<string, string> = {
    success: "bg-[var(--c-status-success-fg)]",
    info: "bg-[var(--c-status-info-fg)]",
    error: "bg-[var(--c-status-error-fg)]",
    warn: "bg-[var(--c-status-warn-fg)]",
  };
  return (
    <div className="w-full h-full p-4 flex gap-2">
      <div className="w-1/2 flex flex-col gap-1">
        {ROWS.map((r, n) => (
          <div
            key={r.headline}
            className={cn(
              "bg-white border px-1.5 py-1 flex items-start gap-1",
              n === 1 && "ring-1 ring-[var(--brand-ink)]",
            )}
            style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
          >
            <span className={cn("h-1.5 w-1.5 rounded-full mt-1 shrink-0", DOT[r.tone])} />
            <div className="min-w-0 flex-1">
              <p className="text-[8px] font-medium text-[var(--ink)] truncate leading-tight">
                {r.headline}
              </p>
              <p className="text-[7px] text-[var(--brand-muted)] truncate leading-tight mt-px">
                {r.meta}
              </p>
            </div>
          </div>
        ))}
      </div>
      <div
        className="flex-1 bg-white border p-1.5 flex flex-col gap-1"
        style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
      >
        <div className="h-2 w-3/4 bg-[var(--brand-secondary)]" />
        <div className="h-1 w-1/2 bg-[var(--brand-secondary)]/60" />
        <div className="mt-1 grid grid-cols-2 gap-x-1 gap-y-0.5">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-1">
              <div className="h-1 w-3 bg-[var(--brand-secondary)]" />
              <div className="h-1 flex-1 bg-[var(--brand-secondary)]/60" />
            </div>
          ))}
        </div>
        <div
          className="mt-auto h-2 w-full"
          style={{
            backgroundColor: "color-mix(in oklab, var(--c-status-success-fg) 18%, transparent)",
            borderRadius: "var(--c-radius-sm)",
          }}
        />
      </div>
    </div>
  );
}

function AccessListsThumb() {
  return (
    <div className="w-full h-full p-4 flex flex-col gap-2">
      <div className="bg-[var(--c-status-warn-bg)] h-5 px-2 flex items-center text-[8px] font-semibold text-[var(--c-status-warn-fg)]" style={{ borderRadius: "var(--c-radius-sm)" }}>
        manual · 逐次人工审批
      </div>
      <div className="bg-white border" style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}>
        <div className="px-1.5 pt-1.5 pb-0">
          <div className="inline-flex p-0.5 bg-[var(--brand-secondary)]/60" style={{ borderRadius: "var(--c-radius-sm)" }}>
            <div className="bg-white px-1.5 py-0.5 text-[8px] font-semibold c-ink-border">Responder 白</div>
            <div className="px-1.5 py-0.5 text-[8px] text-[var(--brand-muted)]">Hotline 白</div>
            <div className="px-1.5 py-0.5 text-[8px] text-[var(--brand-muted)]">黑名单</div>
          </div>
        </div>
        <div className="border-t mt-1.5 px-2 py-1.5 flex flex-col gap-0.5" style={{ borderColor: "var(--border)" }}>
          {["my-company-bot", "responder-local-1", "ops.notify@v1"].map((s) => (
            <div key={s} className="font-mono text-[8.5px] text-[var(--ink)]">{s}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ApprovalsThumb() {
  return (
    <div className="w-full h-full p-4 flex flex-col gap-1.5">
      <div
        className="bg-[color-mix(in_oklab,var(--brand-yellow)_22%,transparent)] border-l-[2px] border-l-[var(--brand-yellow)] c-ink-border h-5 px-2 flex items-center gap-1 text-[8px] font-semibold text-[var(--ink)]"
        style={{ borderRadius: "var(--c-radius-sm)" }}
      >
        <span>⚠</span>
        <span className="truncate">3 待审批 — 一键放行信任 hotline</span>
      </div>
      {[
        { tone: "error", title: "支付扣款 · ¥420", risk: "高" },
        { tone: "warn", title: "数据库查询 · users 表", risk: "中" },
        { tone: "success", title: "日历创建 · 客户拜访", risk: "低" },
      ].map((r) => (
        <div
          key={r.title}
          className="bg-white border px-1.5 py-1 flex items-center gap-1.5"
          style={{
            borderColor: "var(--border)",
            borderRadius: "var(--c-radius-sm)",
            borderLeft: r.tone === "error" ? "2px solid var(--c-status-error-fg)" : undefined,
          }}
        >
          <span
            className={cn(
              "h-1.5 w-1.5 rounded-full shrink-0",
              r.tone === "error" && "bg-[var(--c-status-error-fg)]",
              r.tone === "warn" && "bg-[var(--c-status-warn-fg)]",
              r.tone === "success" && "bg-[var(--c-status-success-fg)]",
            )}
          />
          <p className="text-[8px] font-medium text-[var(--ink)] truncate flex-1 leading-tight">{r.title}</p>
          <span
            className={cn(
              "px-1 py-px text-[7px] font-semibold",
              r.tone === "error" && "c-status-error",
              r.tone === "warn" && "c-status-warn",
              r.tone === "success" && "c-status-success",
            )}
            style={{ borderRadius: "var(--c-radius-pill)" }}
          >
            {r.risk}
          </span>
        </div>
      ))}
    </div>
  );
}

function CatalogThumb() {
  return (
    <div className="w-full h-full p-4 flex gap-2">
      <div className="w-[42%] flex flex-col gap-1">
        <div
          className="bg-white border h-3 flex items-center px-1.5 text-[7px] text-[var(--brand-muted)]"
          style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
        >
          🔍 search hotline…
        </div>
        {[
          { name: "calendar.create-event", n: 1, active: true },
          { name: "db.query", n: 2 },
          { name: "slack.notify", n: 3 },
          { name: "web-search", n: 4 },
        ].map((h) => (
          <div
            key={h.name}
            className={cn(
              "bg-white border px-1.5 py-1",
              h.active && "ring-1 ring-[var(--brand-ink)]",
            )}
            style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
          >
            <p className="font-mono text-[8px] text-[var(--ink)] truncate leading-tight">{h.name}</p>
            <p className="text-[7px] text-[var(--brand-muted)] leading-tight mt-px">v1 · official</p>
          </div>
        ))}
      </div>
      <div
        className="flex-1 bg-white border p-2 flex flex-col gap-1.5"
        style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
      >
        <div className="h-2 w-2/3 bg-[var(--brand-secondary)]" />
        <div className="h-1 w-full bg-[var(--brand-secondary)]/60" />
        <div className="mt-1 flex flex-col gap-1">
          {[0, 1].map((i) => (
            <div key={i} className="space-y-0.5">
              <div className="h-1 w-1/3 bg-[var(--brand-secondary)]/70" />
              <div
                className="h-3 w-full border bg-[var(--brand-secondary)]/30"
                style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
              />
            </div>
          ))}
        </div>
        <div
          className="mt-auto h-3 w-2/5 bg-[var(--brand-green)] c-ink-border flex items-center justify-center"
          style={{ borderRadius: "var(--c-radius-sm)" }}
        >
          <span className="text-[7px] font-bold text-[var(--ink)]">试拨</span>
        </div>
      </div>
    </div>
  );
}

function HelpThumb() {
  return (
    <div className="w-full h-full p-4 flex gap-2">
      <div
        className="w-1/3 bg-white border p-1.5 flex flex-col gap-1"
        style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
      >
        <span className="text-[7px] font-semibold tracking-widest uppercase text-[var(--brand-muted)]">contents</span>
        {[
          { t: "什么是 caller / responder", on: false },
          { t: "5 分钟跑通第一次", on: true },
          { t: "审批与白名单", on: false },
          { t: "故障排查", on: false },
          { t: "反馈渠道", on: false },
        ].map((row) => (
          <div
            key={row.t}
            className={cn(
              "text-[7.5px] truncate leading-tight",
              row.on ? "font-semibold text-[var(--ink)]" : "text-[var(--brand-muted)]",
            )}
          >
            {row.on ? "▸ " : ""}
            {row.t}
          </div>
        ))}
      </div>
      <div className="flex-1 flex flex-col gap-1.5">
        <div className="h-2.5 w-3/5 bg-[var(--brand-ink)]" style={{ borderRadius: "var(--c-radius-sm)" }} />
        <div className="h-1 w-full bg-[var(--brand-secondary)]/70" />
        <div className="h-1 w-5/6 bg-[var(--brand-secondary)]/70" />
        <div
          className="mt-1 h-6 w-full border-l-[2px] border-l-[var(--brand-orange)] bg-[color-mix(in_oklab,var(--brand-orange)_8%,transparent)] px-1 py-0.5"
          style={{ borderRadius: "var(--c-radius-sm)" }}
        >
          <div className="h-1 w-1/3 bg-[var(--ink)]/60 mb-0.5" />
          <div className="h-1 w-2/3 bg-[var(--ink)]/30" />
        </div>
        <div className="mt-auto flex gap-1">
          <div
            className="h-3 px-2 bg-[var(--brand-green)] c-ink-border flex items-center"
            style={{ borderRadius: "var(--c-radius-sm)" }}
          >
            <span className="text-[7px] font-bold text-[var(--ink)]">打开 Catalog</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function TransportThumb() {
  return (
    <div className="w-full h-full p-4 flex flex-col gap-1.5">
      <div className="grid grid-cols-3 gap-1">
        {[
          { l: "local", on: true },
          { l: "relay_http", on: false },
          { l: "email", on: false },
        ].map((t) => (
          <div
            key={t.l}
            className={cn(
              "bg-white border px-1 py-1.5 text-center",
              t.on && "ring-1 ring-[var(--brand-ink)]",
            )}
            style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
          >
            <p className="font-mono text-[7.5px] font-semibold leading-tight">{t.l}</p>
          </div>
        ))}
      </div>
      <div
        className="bg-[var(--brand-secondary)]/40 border p-1.5 flex-1 font-mono text-[7px] text-[var(--ink)]/70 leading-tight"
        style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
      >
        {`{ "transport": "local" }`}
      </div>
      <div className="flex gap-1">
        <div
          className="h-3 px-2 bg-[var(--brand-green)] c-ink-border flex items-center"
          style={{ borderRadius: "var(--c-radius-sm)" }}
        >
          <span className="text-[7px] font-bold">保存</span>
        </div>
        <div
          className="h-3 px-2 bg-white border flex items-center"
          style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
        >
          <span className="text-[7px] font-bold text-[var(--brand-muted)]">测试连接</span>
        </div>
      </div>
    </div>
  );
}

function RuntimeThumb() {
  return (
    <div className="w-full h-full p-4 flex flex-col gap-1.5">
      <div className="flex gap-2 text-[7.5px] font-semibold border-b" style={{ borderColor: "var(--border)" }}>
        <span className="border-b-2 border-[var(--brand-ink)] -mb-px pb-0.5">caller</span>
        <span className="text-[var(--brand-muted)]">responder</span>
        <span className="text-[var(--brand-muted)]">relay</span>
      </div>
      <div className="grid grid-cols-2 gap-1">
        {[1, 2].map((n) => (
          <div
            key={n}
            className="bg-white border px-1.5 py-1"
            style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
          >
            <div className="flex items-center gap-1">
              <span className="h-1 w-1 rounded-full bg-[var(--c-status-success-fg)]" />
              <span className="font-mono text-[7px] truncate">caller-runtime</span>
            </div>
            <p className="text-[6.5px] text-[var(--brand-muted)] mt-0.5">PID 49210 · 3h 12m</p>
          </div>
        ))}
      </div>
      <div className="flex-1 bg-[#1a1a1a] p-1 font-mono text-[6.5px] leading-tight overflow-hidden">
        <div className="text-[#7dd3fc]">14:08 INFO  GET /status 200</div>
        <div className="text-[#fbbf24]">14:08 WARN  approval timeout 30s ×2</div>
        <div className="text-[#f87171]">14:07 ERROR ECONNREFUSED 8092</div>
      </div>
    </div>
  );
}

function PreferencesThumb() {
  return (
    <div className="w-full h-full p-4 flex flex-col gap-1.5">
      <div className="grid grid-cols-3 gap-1">
        {[
          { l: "manual", on: false },
          { l: "allow_listed", on: true },
          { l: "allow_all", on: false },
        ].map((m) => (
          <div
            key={m.l}
            className={cn(
              "bg-white border px-1 py-1 text-center",
              m.on && "ring-1 ring-[var(--brand-ink)]",
            )}
            style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
          >
            <p className="font-mono text-[7px] font-semibold leading-tight">{m.l}</p>
          </div>
        ))}
      </div>
      <div
        className="bg-white border px-1.5 py-1 flex items-center justify-between"
        style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
      >
        <div className="flex gap-2 text-[7px]">
          <span><strong className="text-[var(--ink)]">3</strong> Resp</span>
          <span><strong className="text-[var(--ink)]">7</strong> Hot</span>
          <span className="text-[var(--c-status-error-fg)]"><strong>1</strong> Block</span>
        </div>
        <span className="text-[6.5px] text-[var(--brand-muted)]">名单 →</span>
      </div>
      <div
        className="flex-1 bg-white border p-1.5 flex flex-col gap-0.5"
        style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
      >
        <div className="text-[6.5px] uppercase tracking-wider text-[var(--brand-muted)] mb-0.5">自动路由</div>
        {["search-the-web → bing.v1", "summarize → workspace.v1", "notify → ops.v1"].map((r) => (
          <div key={r} className="font-mono text-[7px] text-[var(--ink)]/80 truncate">{r}</div>
        ))}
      </div>
    </div>
  );
}

function HotlinesThumb() {
  return (
    <div className="w-full h-full p-4 flex flex-col gap-1">
      {[
        { name: "工作区文档摘要", id: "local.workspace-summary.v1", on: true, badge: "official" },
        { name: "Slack 通知 · #ops", id: "ops.notify@v1", on: true, badge: "draft" },
        { name: "Google 日历 · 创建事件", id: "calendar.create-event", on: false, badge: "需修改" },
      ].map((h) => (
        <div
          key={h.id}
          className="bg-white border px-1.5 py-1 flex items-center gap-1"
          style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <p className="text-[7.5px] font-semibold text-[var(--ink)] truncate">{h.name}</p>
              <span
                className={cn(
                  "px-0.5 text-[6px] font-bold uppercase",
                  h.badge === "official" && "bg-[var(--c-status-success-bg)] text-[var(--c-status-success-fg)]",
                  h.badge === "draft" && "bg-[var(--c-status-info-bg)] text-[var(--c-status-info-fg)]",
                  h.badge === "需修改" && "bg-[var(--c-status-warn-bg)] text-[var(--c-status-warn-fg)]",
                )}
                style={{ borderRadius: "var(--c-radius-sm)" }}
              >
                {h.badge}
              </span>
            </div>
            <p className="font-mono text-[6.5px] text-[var(--brand-muted)] truncate mt-px">{h.id}</p>
          </div>
          <div
            className={cn(
              "h-2 px-1 border text-[6px] font-bold flex items-center",
              h.on ? "bg-[var(--brand-green)] border-[var(--brand-ink)]" : "bg-white border-[var(--border)] text-[var(--brand-muted)]",
            )}
            style={{ borderRadius: "var(--c-radius-sm)" }}
          >
            {h.on ? "ON" : "OFF"}
          </div>
        </div>
      ))}
    </div>
  );
}

function PublishThumb() {
  const COLS: { l: string; tone: string; n: number }[] = [
    { l: "草稿", tone: "var(--brand-muted)", n: 1 },
    { l: "审核中", tone: "var(--brand-blue)", n: 1 },
    { l: "需修改", tone: "var(--c-status-warn-fg)", n: 1 },
    { l: "已上架", tone: "var(--c-status-success-fg)", n: 1 },
  ];
  return (
    <div className="w-full h-full p-4 grid grid-cols-4 gap-1">
      {COLS.map((c, i) => (
        <div
          key={c.l}
          className="bg-white border flex flex-col"
          style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
        >
          <div
            className="px-1 py-0.5 border-b flex items-center gap-1"
            style={{ borderColor: "var(--border)" }}
          >
            <span className="h-1 w-1 rounded-full" style={{ backgroundColor: c.tone }} />
            <span className="text-[7px] font-semibold flex-1">{c.l}</span>
            <span className="text-[6.5px] text-[var(--brand-muted)]">{c.n}</span>
          </div>
          <div className="flex-1 p-0.5">
            <div
              className={cn(
                "bg-[var(--brand-secondary)]/40 h-full border",
                i === 2 && "ring-1 ring-[var(--brand-ink)]",
              )}
              style={{ borderColor: "var(--border)", borderRadius: "var(--c-radius-sm)" }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
